import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';


interface AccountHead {
  ah_id: number;
  ah_name: string;
  ah_parent: string | number;
}


interface AhModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        ah_id?: number;
        ah_name?: string;
        ah_parent?: number;
    } | null;
    isEditMode?: boolean;
}

const AhModal: React.FC<AhModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);
    const [accountHeads, setAccountHeads] = useState<AccountHead[]>([]);



    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    const fetchAccountHeads = () => {
    apiRequest({ method: 'get', url: 'account-heads?ah_parent=0&status=1' })
        .then(data => setAccountHeads(data))
        .catch(() => showError('Failed to load account head groups'));
    };

    useEffect(() => {
    fetchAccountHeads(); // initial load
    }, []);


    // Handle form submit
    const handleAddAh = async (values: any) => {
        try {
            const dataVal = {
                ah_name: values.ah_name,
                ah_parent: values.ah_parent,
                ...(initialValues?.ah_id && { ah_id: initialValues.ah_id }),
            };
            const url = isEditMode
                ? 'edit-account-head'
                : 'add-account-head';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Account Head updated successfully!' : 'Account Head created successfully!',
            );
            form.resetFields();

            // ✅ ✅ fetch updated list
            fetchAccountHeads(); 

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} Account Head`);
        }
    };

    // // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                ah_name: initialValues.ah_name,
                ah_parent: initialValues.ah_parent,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);

    return (
        <Modal
            title={isEditMode ? 'Edit Account Head' : 'Add New Account Head'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddAh}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="ah_parent"
                            label={
                                <span>
                                    Account Head Parent &nbsp;
                                </span>
                            }
                            rules={[{ required: true, message: 'Please select account head parent' }]}
                        >
                            <Select
                                placeholder="Select account head parent"
                                options={[
                                { label: 'Root', value: 0 }, // ✅ Default static option
                                ...accountHeads.map(ah => ({
                                label: ah.ah_name, // 👈 show readable name
                                value: ah.ah_id,   // 👈 send ID to backend
                                }))
                            ]}
                            />


                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ah_name"
                            className='px-8'
                            label={
                                <span>
                                    Account Head &nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 50 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the Account Head Name' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                            getValueFromEvent={(e) => {
                                let value = e.target.value;
                                // First letter ko capitalize karna
                                if (value.length > 0) {
                                    value = value.charAt(0).toUpperCase() + value.slice(1);
                                }
                                return value;
                            }}
                        >
                            <Input
                                showCount
                                maxLength={50}
                                placeholder="e.g., Account Head "
                            />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Account Head
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AhModal;
